import { LEGACY_URLS, URLS } from 'Douleutaras/constants/urls';
import { getDeepValue } from 'Douleutaras/utilities/helpers/objects.helper';
import { trimBy } from 'Douleutaras/utilities/helpers/strings.helper';
import { addLanguageUrlPrefix, getCountryCode, getCurrentLanguage, isAnonymizedPathname } from 'Douleutaras/hooks/localization';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';

export enum VALID_URLS {
  PROFILE,
  MATCHING_ALGO,
  CATEGORY,
  MUNICIPALITY,
  CLIENT_DASHBOARD_JOB,
  CONVERSATION_THREAD,
  CONVERSATION_META,
  PRO_CONVERSATION_THREAD,
  PRO_CONVERSATION_META,
  LEGACY_JOB,
  TRANSACTION_INVOICE_PAYMENT,
  PLANS,
  SUPPORT_CLEANING_JOB,
  PROS_JOB,
  PROS_JOB_OUTER,
  PROS_STATUS_UPDATE,
  CLIENT_DASHBOARD_CCF_NEED_HELP,
  CLIENT_DASHBOARD_CCF_CANCEL,
  PROS_EXTERNAL_STATUS_UPDATE,
  CLIENT_PAYMENT_REQUEST_RECEIVED,
  CUSTOMER_ONLINE_PAYMENT,
  CREATE_CUSTOMER_ONLINE_PAYMENT,
  CONTACT_CUSTOMER,
  PROS_SUSPICIOUS_REPORT,
  JOB_DONE,
  EXTRA_BID_FOR_JOB,
  CLIENT_REVIEW_BID,
  CLIENT_REVIEW_NPS,
  BSDIR_CATEGORY,
  BSDIR_INDEX,
  BSDIR_PAGE,
  PROS_SETTINGS_SERVICE_PRICE_NEW,
  PROS_SETTINGS_SERVICE_PRICE_EDIT
}

/**
 * Adds the current-language URL prefix (e.g. /en) to anonymized-route paths (see ANONYMIZED_PATHS)
 * when the page is currently being viewed in the prefixed language — so links built while viewing
 * the /en variant of an anonymized page keep pointing at the /en variant, instead of always
 * dropping back to the country default.
 */
const withLanguageUrlPrefix = (path: string): string => {
  if (!isAnonymizedPathname(path)) {
    return path;
  }
  const countryCode = getCountryCode();
  const prefixedLanguage = COUNTRY_CONFIG.PREFIXED_LANGUAGE[countryCode];
  // IE's default language is its prefixed one, so its bare URL already serves it
  const isPrefixedLanguage = prefixedLanguage !== COUNTRY_CONFIG.DEFAULT_LANGUAGE[countryCode]
    && getCurrentLanguage() === prefixedLanguage;

  return isPrefixedLanguage ? addLanguageUrlPrefix(path) : path;
};

/**
 * Utility to build URLs based on predefined constants
 */
export const buildUrl = (url: VALID_URLS, ...identifiers) => withLanguageUrlPrefix(buildRawUrl(url, ...identifiers));

const buildRawUrl = (url: VALID_URLS, ...identifiers) => {
  switch (url) {
    case VALID_URLS.PROFILE:
      return URLS.PRO_PROFILE.INDEX.replace(':username', identifiers[0]);
    case VALID_URLS.MATCHING_ALGO:
      return URLS.JOB.MATCHING_ALGO
        .replace(':jobCategory', identifiers[0])
        .replace(':jobId', identifiers[1]);
    case VALID_URLS.CATEGORY:
      return URLS.CATEGORY.replace(':catId', identifiers[0]);
    case VALID_URLS.MUNICIPALITY:
      return URLS.MUNICIPALITY.replace(':munSlug', identifiers[0]);
    case VALID_URLS.CLIENT_DASHBOARD_JOB:
      return getAbsoluteUrl('CLIENT_DASHBOARD.JOB.INDEX')
        .replace(':jobId', identifiers[0]);
    case VALID_URLS.CLIENT_DASHBOARD_CCF_NEED_HELP:
      return URLS.CLIENT_DASHBOARD.CCF_MODALS.NEED_HELP.replace(':jobId', identifiers[0]);
    case VALID_URLS.CLIENT_DASHBOARD_CCF_CANCEL:
      return URLS.CLIENT_DASHBOARD.CCF_MODALS.CANCEL.replace(':jobId', identifiers[0]);
    case VALID_URLS.CONVERSATION_THREAD:
      return getAbsoluteUrl('CLIENT_DASHBOARD.CONVERSATION.THREAD')
        .replace(':conversationId', identifiers[0]);
    case VALID_URLS.PRO_CONVERSATION_THREAD:
      return getAbsoluteUrl('PRO_DASHBOARD.CONVERSATION.THREAD')
        .replace(':conversationId', identifiers[0]);
    case VALID_URLS.CONVERSATION_META:
      return getAbsoluteUrl('CLIENT_DASHBOARD.CONVERSATION.META')
        .replace(':conversationId', identifiers[0]);
    case VALID_URLS.PRO_CONVERSATION_META:
      return getAbsoluteUrl('PRO_DASHBOARD.CONVERSATION.META')
        .replace(':conversationId', identifiers[0]);
    case VALID_URLS.LEGACY_JOB:
      return LEGACY_URLS.JOB.replace(':jobId', identifiers[0]);
    case VALID_URLS.TRANSACTION_INVOICE_PAYMENT:
      return `${getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.INVOICE_PAYMENT')}?invoice=${identifiers[0]}&method=${identifiers[1]}`;
    case VALID_URLS.PLANS:
      return getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.PLANS.PAYMENT')
        .replace(':plan', identifiers[0])
        .replace(':backend', identifiers[1])
        .replace(':backend_type', identifiers[2]);
    case VALID_URLS.SUPPORT_CLEANING_JOB:
      return URLS.SUPPORT.CLEANING_JOB.replace(':jobId', identifiers[0]);
    case VALID_URLS.PROS_JOB:
      return getAbsoluteUrl('PRO_DASHBOARD.JOB_PAGE.INDEX')
        .replace(':bidId', identifiers[0]);
    case VALID_URLS.PROS_JOB_OUTER:
      return getAbsoluteUrl('PRO_DASHBOARD.JOB_PAGE_OUTER')
        .replace(':outerJobId', identifiers[0]);
    case VALID_URLS.PROS_STATUS_UPDATE:
      return getAbsoluteUrl('PRO_DASHBOARD.STATUS_UPDATE')
        .replace(':bidId', identifiers[0]);
    case VALID_URLS.PROS_EXTERNAL_STATUS_UPDATE:
      return getAbsoluteUrl('PRO_DASHBOARD.STATUS_UPDATE_EXTERNAL')
        .replace(':jobId', identifiers[0]);
    case VALID_URLS.CLIENT_PAYMENT_REQUEST_RECEIVED:
      return getAbsoluteUrl('CLIENT_DASHBOARD.JOB.PAYMENT_REQUEST_RECEIVED')
        .replace(':jobId', identifiers[0])
        .replace(':reqId', identifiers[1]);
    case VALID_URLS.CUSTOMER_ONLINE_PAYMENT:
      return getAbsoluteUrl('PRO_DASHBOARD.JOB_PAGE.CUSTOMER_ONLINE_PAYMENT')
        .replace(':bidId', identifiers[0])
        .replace(':payId', identifiers[1]);
    case VALID_URLS.JOB_DONE:
      return getAbsoluteUrl('JOB_DONE')
        .replace(':jobId', identifiers[0]);
    case VALID_URLS.CREATE_CUSTOMER_ONLINE_PAYMENT:
      return getAbsoluteUrl('CLIENT_DASHBOARD.JOB.CREATE_ONLINE_PAYMENT')
        .replace(':jobId', identifiers[0])
        .replace(':bidId', identifiers[1]);
    case VALID_URLS.CONTACT_CUSTOMER:
      return getAbsoluteUrl('PRO_DASHBOARD.JOB_PAGE.CONTACT_CUSTOMER')
        .replace(':bidId', identifiers[0]);
    case VALID_URLS.PROS_SUSPICIOUS_REPORT:
      return getAbsoluteUrl('PRO_DASHBOARD.JOB_PAGE.SUSPICIOUS_REPORT')
        .replace(':bidId', identifiers[0]);
    case VALID_URLS.PROS_SETTINGS_SERVICE_PRICE_NEW:
      return getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.PROFILE_SERVICES.PRICE_NEW')
        .replace(':categoryId', identifiers[0]);
    case VALID_URLS.PROS_SETTINGS_SERVICE_PRICE_EDIT:
      return getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.PROFILE_SERVICES.PRICE_EDIT')
        .replace(':priceId', identifiers[0]);
    case VALID_URLS.EXTRA_BID_FOR_JOB:
      return getAbsoluteUrl('CLIENT_DASHBOARD.PRO_LIST.EXTRA_BID')
        .replace(':jobId', identifiers[0]);
    case VALID_URLS.CLIENT_REVIEW_BID:
      return getAbsoluteUrl('CLIENT_DASHBOARD.JOB.REVIEW_BID')
        .replace(':jobId', identifiers[0])
        .replace(':bidId', identifiers[1]);
    case VALID_URLS.CLIENT_REVIEW_NPS:
      return getAbsoluteUrl('CLIENT_DASHBOARD.JOB.REVIEW_NPS')
        .replace(':jobId', identifiers[0]);
    case VALID_URLS.BSDIR_CATEGORY:
      return URLS.BSDIR_CATEGORY.replace(':categorySlug', identifiers[0]);
    case VALID_URLS.BSDIR_INDEX:
      return URLS.BSDIR.INDEX;
    case VALID_URLS.BSDIR_PAGE:
      return URLS.BSDIR.PAGE
        .replace(':groupSlug', identifiers[0])
        .replace(':pageSlug', identifiers[1]);

    default:
      throw new Error('You need to pass a Valid URL into buildUrl utility');
  }
};

/**
 * This function will always return an absolute url by the given path name.
 * It will break @path into the sections divided by "." and concatenate it.
 * If a section has INDEX, it would be added to the beginning of a section.
 *
 * Example:
 * const URL = {
 *   CLIENT_DASHBOARD: {
 *     INDEX: '/dashboard-v2',
 *     CONVERSATION: {
 *       INDEX: 'inbox',
 *       THREAD: 'conversation/:conversationId',
 *     }
 *   },
 *   PRO_DASHBOARD: {
 *     INDEX: '/dashboard-v2/pro',
 *     CONVERSATION: {
 *       INDEX: 'inbox'
 *     }
 *   }
 * };
 * getAbsoluteUrl('CLIENT_DASHBOARD.CONVERSATION.THREAD'); // /dashboard-v2/inbox/conversation/:conversationId
 * Since CLIENT_DASHBOARD has INDEX and
 * CONVERSATION has INDEX the formula would be:
 * CLIENT_DASHBOARD.CONVERSATION.THREAD = CLIENT_DASHBOARD.INDEX + CONVERSATION.INDEX + CONVERSATION.THREAD
 *
 * getAbsoluteUrl('PRO_DASHBOARD.CONVERSATION.INDEX', true); // /dashboard-v2/pro/inbox/*
 * Formula:
 * PRO_DASHBOARD.CONVERSATION.INDEX = PRO_DASHBOARD.INDEX + CONVERSATION.INDEX + '/*' (because @parent equal true)
 *
 * @param path String example "PRO_DASHBOARD.JOB_PAGE"
 * @param parent Boolean
 */
export const getAbsoluteUrl = (path: string, parent = false): string => {
  const relativeValue = getDeepValue(URLS, path);

  if (relativeValue[0] === '/') {
    return relativeValue;
  }
  let p = path;
  const parts = path.split('.');

  let absolutePath = '';
  let i = parts.length - 1;

  while (i > 0) {
    p = p.replace(`.${parts[i]}`, '');
    if (parts[i] !== 'INDEX') {
      const tmp = getDeepValue(URLS, `${p}.INDEX`);
      if (typeof tmp === 'string') {
        absolutePath = `${trimBy(tmp, '/')}/${trimBy(absolutePath, '/')}`;
      }
    }
    i--;
  }

  return `/${trimBy(absolutePath, '/')}/${relativeValue}${parent ? '/*' : ''}`;
};
